import React, { useEffect, useState } from "react";
import {
  FiChevronDown,
  FiDownload,
  FiExternalLink,
  FiFile,
  FiFileText,
  FiImage,
  FiShare2,
} from "react-icons/fi";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../BaseUI/Modal";
import Button from "../../BaseUI/Button";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import { useAuth } from "../../../services/useAuth";
import {
  createApprovalAttachmentObjectUrl,
  downloadApprovalAttachment,
  isImageAttachment,
  isPdfAttachment,
  isPreviewableAttachment,
  shareApprovalAttachment,
} from "../../../approvals/attachments";
import "./attachments.css";

const formatSize = (size) =>
  !size
    ? ""
    : size < 1024 * 1024
      ? `${Math.max(1, Math.round(size / 1024))} KB`
      : `${(size / 1024 / 1024).toFixed(2)} MB`;

const attachmentMeta = (attachment) =>
  [
    attachment?.stepLabel,
    attachment?.resolvedBy,
    attachment?.resolvedAt
      ? formatInstantDateShortTr(attachment.resolvedAt)
      : "",
    formatSize(attachment?.size),
  ]
    .filter(Boolean)
    .join(" · ");

const AttachmentIcon = ({ attachment }) => {
  if (isImageAttachment(attachment)) return <FiImage />;
  if (isPdfAttachment(attachment)) return <FiFileText />;
  return <FiFile />;
};

/** Attachment viewer for approval steps: previews images and PDFs inline. */
const ApprovalAttachmentsModal = ({
  isOpen,
  onClose,
  attachments = [],
  onShare,
  onDownload,
  title = "Eklenen Dosyalar",
  initialAttachmentId = null,
}) => {
  const { getToken } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const selected =
    attachments.find((item) => item.id === selectedId) ||
    attachments[0] ||
    null;
  const hasRail = attachments.length > 1;

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setPreviewExpanded(false);
      return;
    }
    const preferred =
      attachments.find((item) => item.id === initialAttachmentId) ||
      attachments.find(isPreviewableAttachment) ||
      attachments[0] ||
      null;
    setSelectedId(preferred?.id || null);
    setPreviewExpanded(preferred ? isPreviewableAttachment(preferred) : false);
  }, [isOpen, attachments, initialAttachmentId]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    setPreviewUrl(null);
    setPreviewError("");

    if (
      !isOpen ||
      !previewExpanded ||
      !selected ||
      !isPreviewableAttachment(selected)
    ) {
      setLoadingPreview(false);
      return undefined;
    }

    setLoadingPreview(true);
    (async () => {
      try {
        objectUrl = await createApprovalAttachmentObjectUrl(
          selected,
          getToken(),
        );
        if (cancelled) {
          window.URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl(objectUrl);
      } catch (error) {
        if (!cancelled) {
          setPreviewError(
            error?.response?.data?.message || "Dosya önizlemesi yüklenemedi.",
          );
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, previewExpanded, selected?.id, getToken]);

  const handleShare = (attachment) =>
    onShare
      ? onShare(attachment)
      : shareApprovalAttachment(attachment, getToken());

  const handleDownload = (attachment) =>
    onDownload
      ? onDownload(attachment)
      : downloadApprovalAttachment(attachment, getToken());

  const openInNewTab = async (attachment) => {
    if (!attachment || !isPreviewableAttachment(attachment)) return;
    try {
      if (previewUrl && selected?.id === attachment.id) {
        window.open(previewUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const objectUrl = await createApprovalAttachmentObjectUrl(
        attachment,
        getToken(),
      );
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      // Revoke later so the new tab can finish loading the blob.
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      console.error(error);
    }
  };

  const selectAttachment = (id) => {
    setSelectedId(id);
    const attachment = attachments.find((item) => item.id === id);
    if (attachment && isPreviewableAttachment(attachment)) {
      setPreviewExpanded(true);
    }
  };

  const renderStage = () => {
    if (loadingPreview) {
      return (
        <div className="approval-attachments__state">
          <span className="approval-attachments__spinner" />
          <span>Önizleme yükleniyor...</span>
        </div>
      );
    }
    if (previewError) {
      return (
        <div className="approval-attachments__state">
          <span className="text-danger">{previewError}</span>
        </div>
      );
    }
    if (!isPreviewableAttachment(selected)) {
      return (
        <div className="approval-attachments__state">
          <AttachmentIcon attachment={selected} />
          <span>Bu dosya türü tarayıcıda önizlenemiyor.</span>
          <Button
            appearance="subtle"
            iconBefore={<FiShare2 />}
            onClick={() => handleShare(selected)}
          >
            Paylaş
          </Button>
          <Button
            appearance="subtle"
            onClick={() => handleDownload(selected)}
          >
            İndir
          </Button>
        </div>
      );
    }
    if (!previewUrl) return null;
    if (isImageAttachment(selected)) {
      return <img src={previewUrl} alt={selected.originalName} />;
    }
    return (
      <iframe
        title={selected.originalName}
        src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="x-large"
      scrollBehavior="inside"
    >
      <ModalHeader>
        {attachments.length > 1 ? `${title} (${attachments.length})` : title}
      </ModalHeader>
      <ModalBody>
        {attachments.length === 0 || !selected ? (
          <p className="text-muted mb-0">Bu kayıt için dosya bulunmuyor.</p>
        ) : (
          <div className="approval-attachments">
            <div
              className={
                hasRail
                  ? "approval-attachments__layout"
                  : "approval-attachments__layout approval-attachments__layout--single"
              }
            >
              {hasRail && (
                <div className="approval-attachments__rail">
                  <span className="approval-attachments__rail-label">
                    Dosyalar
                  </span>
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected.id === attachment.id}
                      className={`approval-attachments__item${
                        selected.id === attachment.id
                          ? " approval-attachments__item--active"
                          : ""
                      }`}
                      onClick={() => selectAttachment(attachment.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectAttachment(attachment.id);
                        }
                      }}
                    >
                      <span className="approval-attachments__item-icon">
                        <AttachmentIcon attachment={attachment} />
                      </span>
                      <span className="d-flex flex-column flex-grow-1 min-w-0">
                        <span
                          className="approval-attachments__item-name approval-attachments__item-name--link"
                          title={attachment.originalName}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            selectAttachment(attachment.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              selectAttachment(attachment.id);
                            }
                          }}
                        >
                          {attachment.originalName}
                        </span>
                        <span className="approval-attachments__item-meta">
                          {attachmentMeta(attachment)}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="approval-attachments__icon-button"
                        title="Paylaş"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleShare(attachment);
                        }}
                      >
                        <FiShare2 />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div
                className={`approval-attachments__viewer${
                  previewExpanded
                    ? ""
                    : " approval-attachments__viewer--collapsed"
                }`}
              >
                <div className="approval-attachments__viewer-header">
                  <button
                    type="button"
                    className="approval-attachments__viewer-toggle"
                    aria-expanded={previewExpanded}
                    title={
                      previewExpanded ? "Önizlemeyi kapat" : "Önizlemeyi aç"
                    }
                    onClick={() => setPreviewExpanded((open) => !open)}
                  >
                    <span className="approval-attachments__item-icon">
                      <AttachmentIcon attachment={selected} />
                    </span>
                    <span className="d-flex flex-column">
                      <span
                        className="approval-attachments__viewer-name approval-attachments__item-name--link"
                        title={selected.originalName}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (isPreviewableAttachment(selected)) {
                            setPreviewExpanded(true);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            if (isPreviewableAttachment(selected)) {
                              setPreviewExpanded(true);
                            }
                          }
                        }}
                      >
                        {selected.originalName}
                      </span>
                      <span className="approval-attachments__viewer-meta">
                        {attachmentMeta(selected) || "-"}
                      </span>
                    </span>
                    <FiChevronDown
                      className={`approval-attachments__chevron${
                        previewExpanded
                          ? " approval-attachments__chevron--open"
                          : ""
                      }`}
                    />
                  </button>
                  <div className="d-flex align-items-center gap-2">
                    {isPreviewableAttachment(selected) && (
                      <Button
                        appearance="subtle"
                        iconBefore={<FiExternalLink />}
                        onClick={() => openInNewTab(selected)}
                      >
                        Yeni sekmede aç
                      </Button>
                    )}
                    <Button
                      appearance="subtle"
                      iconBefore={<FiShare2 />}
                      onClick={() => handleShare(selected)}
                    >
                      Paylaş
                    </Button>
                  </div>
                </div>
                {previewExpanded && (
                  <div className="approval-attachments__stage">
                    {renderStage()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="d-flex gap-2">
          {selected && (
            <Button
              appearance="primary-subtle"
              iconBefore={<FiDownload />}
              onClick={() => handleDownload(selected)}
            >
              İndir
            </Button>
          )}
          <Button appearance="danger-subtle" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ApprovalAttachmentsModal;
